import { useState, useEffect } from 'react';
import { AuthVerificationCodeInput } from './AuthVerificationCodeInput';
import { AuthSubmitButton } from './AuthSubmitButton';
import { useInsforge } from '../../provider/InsforgeProvider';
import type { EmailVerificationMethod } from '../../types';
import {
  VerificationStepWrapper,
  VerificationStepDescriptionContainer,
  VerificationStepDescriptionTitle,
  VerificationStepDescription,
  VerificationEmail,
  VerificationStepCodeContainer,
  VerificationStepCodeInputWrapper,
  VerificationStepResendContainer,
  VerificationStepResendButton,
} from '../forms/styled';

interface AuthEmailVerificationStepProps {
  email: string;
  method: EmailVerificationMethod;
  onVerifyCode?: (code: string) => Promise<void>;
  emailSent?: boolean;
}

/**
 * Email verification step component (pure UI, no container)
 *
 * Designed to be embedded within a form container.
 * Handles the email verification flow:
 * - Automatically sends verification email on mount
 * - Shows countdown timer for resend functionality
 * - Manages rate limiting for resend attempts
 * - Supports both code and link verification methods
 *
 * @param method - 'code' for OTP input, 'link' for magic link (default: 'code')
 */
export function AuthEmailVerificationStep({
  email,
  method,
  onVerifyCode,
  emailSent = false,
}: AuthEmailVerificationStepProps) {
  const { resendVerificationEmail } = useInsforge();
  const [resendDisabled, setResendDisabled] = useState(emailSent ? true : false);
  const [resendCountdown, setResendCountdown] = useState(emailSent ? 60 : 0);
  const [isSending, setIsSending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const isLinkMethod = method === 'link';
  // Default descriptions based on method
  const displayDescription = isLinkMethod
    ? "We've sent an email to {email}. Please check your email to confirm your account before signing in. The confirmation link expires in 10 minutes."
    : "We've sent a verification code to your inbox at {email}. Enter it below to proceed.";

  // Countdown timer effect
  useEffect(() => {
    if (resendDisabled && resendCountdown > 0) {
      const timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendDisabled, resendCountdown]);

  const handleResend = async () => {
    setResendDisabled(true);
    setResendCountdown(60);
    setIsSending(true);

    try {
      await resendVerificationEmail(email);
    } catch {
      // If resend fails, re-enable the button immediately
      setResendDisabled(false);
      setResendCountdown(0);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!onVerifyCode || verificationCode.length !== 6) {
      return;
    }

    setIsVerifying(true);

    try {
      await onVerifyCode(verificationCode);
    } finally {
      setIsVerifying(false);
      setVerificationCode('');
    }
  };

  return (
    <VerificationStepWrapper>
      {/* Description with email (only for link method) */}
      {isLinkMethod && (
        <VerificationStepDescriptionContainer>
          <VerificationStepDescriptionTitle>Verify Your Email</VerificationStepDescriptionTitle>
          <VerificationStepDescription>
            {displayDescription.split('{email}').map((part, index, array) => (
              <span key={index}>
                {part}
                {index < array.length - 1 && <VerificationEmail>{email}</VerificationEmail>}
              </span>
            ))}
          </VerificationStepDescription>
        </VerificationStepDescriptionContainer>
      )}

      {/* Description with email + code input (only for code method) */}
      {!isLinkMethod && (
        <VerificationStepCodeContainer>
          <VerificationStepCodeInputWrapper>
            <VerificationStepDescription>
              {displayDescription.split('{email}').map((part, index, array) => (
                <span key={index}>
                  {part}
                  {index < array.length - 1 && <VerificationEmail>{email}</VerificationEmail>}
                </span>
              ))}
            </VerificationStepDescription>
            <AuthVerificationCodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              disabled={isVerifying}
            />
          </VerificationStepCodeInputWrapper>

          <AuthSubmitButton
            type="button"
            isLoading={isVerifying}
            disabled={isVerifying || verificationCode.length !== 6}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isVerifying ? 'Verifying...' : 'Continue'}
          </AuthSubmitButton>
        </VerificationStepCodeContainer>
      )}

      {/* Resend Button */}
      <VerificationStepResendContainer>
        Didn&apos;t receive the email?{' '}
        <VerificationStepResendButton
          onClick={() => {
            void handleResend();
          }}
          disabled={resendDisabled || isSending}
        >
          {isSending
            ? 'Sending...'
            : resendDisabled
              ? `Retry in (${resendCountdown}s)`
              : 'Click to resend'}
        </VerificationStepResendButton>
      </VerificationStepResendContainer>
    </VerificationStepWrapper>
  );
}
